/**
 * MerchantsTab - Admin merchants management tab
 * Full feature parity with inline implementation
 */
import { motion } from 'framer-motion';
import { Store, Loader2, Banknote } from 'lucide-react';
import { Button } from '../../ui/button';
import { useLanguage } from '../../../context/LanguageContext';
import { fadeInUp } from '../adminUtils';

export function MerchantsTab({ 
  merchants, 
  loading, 
  onApprove,
  onOpenPayoutDialog,
  getStatusIcon,
  getStatusColor
}) {
  const { t } = useLanguage();

  return (
    <motion.div 
      variants={fadeInUp} 
      className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" 
      data-testid="merchants-table"
    >
      <div className="px-6 py-4 border-b border-slate-100">
        <h3 className="font-semibold text-slate-900">{t('common.colmado')}s</h3>
      </div>
      {loading ? (
        <div className="p-8 text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" />
        </div>
      ) : merchants.length === 0 ? (
        <div className="p-8 text-center text-slate-500">
          <Store className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p>{t('tables.noMerchantsYet')}</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                  {t('common.colmado')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                  {t('tables.settlementAmount')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                  {t('tables.txns')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                  {t('common.status')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                  {t('common.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {merchants.map((merchant) => (
                <tr 
                  key={merchant.id} 
                  className="hover:bg-slate-50" 
                  data-testid={`merchant-${merchant.id}`}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                        <Store className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <span className="font-medium text-slate-900">
                          {merchant.business_name || 'Unknown'}
                        </span>
                        <p className="text-sm text-slate-500">{merchant.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 font-semibold text-slate-900">
                    ${(merchant.wallet_balance || 0).toFixed(2)}
                  </td>
                  <td className="px-6 py-4 text-slate-600">
                    {merchant.transaction_count || 0}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(merchant.status)}`}>
                      {getStatusIcon && getStatusIcon(merchant.status)}
                      {merchant.status || 'unknown'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      {merchant.status === 'pending' && onApprove && (
                        <Button 
                          size="sm" 
                          onClick={() => onApprove(merchant.id)}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                          Approve
                        </Button>
                      )}
                      {merchant.status === 'active' && merchant.wallet_balance > 0 && onOpenPayoutDialog && (
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => onOpenPayoutDialog(merchant)}
                        >
                          <Banknote className="w-4 h-4 mr-1" />
                          Payout
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </motion.div>
  );
}

export default MerchantsTab;
